import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import backend from 'utils/backend';

import {Box, Button, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';
import {CheckCircleOutlined, ErrorOutlineOutlined, ScheduleOutlined, ChatOutlined} from 'views/icons';
import {useNavigate} from 'react-router-dom';


const RoutinesView = (props) => {
  const {routine, onRunNow, onEdit, onToggleStatus, onDelete} = props;
  const navigate = useNavigate();
  const [runHistory, setRunHistory] = React.useState([]);
  const routineRn = routine?.routineRn;
  const lastRunAt = routine?.lastRunAt;
  const runningCount = runHistory.filter((run) => run.status === 'RUNNING').length;

  React.useEffect(() => {
    let isMounted = true;

    if (!routineRn) {
      setRunHistory([]);
      return () => {
        isMounted = false;
      };
    }

    (async () => {
      try {
        const response = await backend.routine.getRunHistory(routineRn);
        if (isMounted) {
          setRunHistory(Array.isArray(response?.list) ? response.list : []);
        }
      } catch {
        if (isMounted) {
          setRunHistory([]);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
    // lastRunAt is included so a "Run now" (which reloads the routine and bumps lastRunAt)
    // refreshes the history feed, not just the routineRn switch.
  }, [routineRn, lastRunAt]);

  const SCHEDULE_LABEL = {
    hourly: 'Hourly',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    custom: 'Custom',
    one_time: 'One-time',
  };

  const DOW = {SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6};

  const computeNextRunAt = (schedule, lastRunAt) => {
    if (!schedule) return null;
    const type = (schedule.type || '').toUpperCase();
    const now = moment();

    switch (type) {
      case 'ONE_TIME': {
        if (!schedule.datetime) return null;
        const fireAt = moment(schedule.datetime + 'Z');
        return fireAt.isAfter(now) ? fireAt.toISOString() : null;
      }
      case 'HOURLY': {
        const candidate = now.clone().startOf('hour').minute(schedule.minute ?? 0);
        if (candidate.isAfter(now)) return candidate.toISOString();
        return candidate.add(1, 'hour').toISOString();
      }
      case 'DAILY': {
        if (!schedule.time) return null;
        const [h, m] = schedule.time.split(':').map(Number);
        const candidate = now.clone().startOf('day').hour(h).minute(m);
        if (candidate.isAfter(now)) return candidate.toISOString();
        return candidate.add(1, 'day').toISOString();
      }
      case 'WEEKLY': {
        if (!schedule.time || !schedule.dayOfWeek) return null;
        const [h, m] = schedule.time.split(':').map(Number);
        const targetDow = DOW[(schedule.dayOfWeek || '').toUpperCase()] ?? 1;
        const candidate = now.clone().day(targetDow).startOf('day').hour(h).minute(m);
        if (candidate.isAfter(now)) return candidate.toISOString();
        return candidate.add(1, 'week').toISOString();
      }
      case 'MONTHLY': {
        if (!schedule.time || !schedule.dayOfMonth) return null;
        const [h, m] = schedule.time.split(':').map(Number);
        const candidate = now.clone().date(schedule.dayOfMonth).startOf('day').hour(h).minute(m);
        if (candidate.isAfter(now)) return candidate.toISOString();
        return candidate.add(1, 'month').toISOString();
      }
      case 'CUSTOM': {
        const match = (schedule.expression || '').match(/^rate\((\d+)\s+(minutes?|hours?|days?)\)$/i);
        if (match) {
          const n = parseInt(match[1], 10);
          const unit = match[2].toLowerCase().startsWith('day') ? 'days' :
            match[2].toLowerCase().startsWith('hour') ? 'hours' : 'minutes';
          return (lastRunAt ? moment(lastRunAt) : now).clone().add(n, unit).toISOString();
        }
        return null;
      }
      default:
        return null;
    }
  };

  // Nest subroutine job entries beneath the routine-run session that spawned them.
  // A subroutine's parentRn is a session RN when the routine queued it directly (BYPASS_PERMISSIONS
  // fan-out); when a subroutine instead came from a human running Chanel on an approval task, its
  // parentRn is the task RN, which isn't a run-history session — those stay top-level, same as before.
  // TODO: this grouping should be done in the backend (GET /routine/{rn}/run_history should return
  // the tree already nested) instead of reconstructed client-side from the flat list.
  const groupedRunHistory = React.useMemo(() => {
    const topLevelBySessionRn = new Map(
        runHistory
            .filter((run) => run.kind !== 'SUBROUTINE_JOB' && run.sessionRn)
            .map((run) => [run.sessionRn, run]),
    );
    const childrenByParentSessionRn = new Map();
    const topLevel = [];

    runHistory.forEach((run) => {
      const parentRun = run.kind === 'SUBROUTINE_JOB' ? topLevelBySessionRn.get(run.parentRn) : null;
      if (!parentRun) {
        topLevel.push(run);
        return;
      }
      const children = childrenByParentSessionRn.get(parentRun.sessionRn) || [];
      children.push(run);
      childrenByParentSessionRn.set(parentRun.sessionRn, children);
    });

    return topLevel.map((run) => ({
      run,
      children: run.sessionRn ? (childrenByParentSessionRn.get(run.sessionRn) || []) : [],
    }));
  }, [runHistory]);

  const formatTs = (ts) => {
    if (!ts) return null;
    const m = moment(ts);
    return m.isValid() ? m.format('MMM D, YYYY, HH:mm') : ts;
  };

  const formatSchedule = (schedule) => {
    if (!schedule) return '—';
    const type = (schedule.type || '').toLowerCase();
    const base = SCHEDULE_LABEL[type] || schedule.type;
    if (type === 'one_time') {
      if (!schedule.datetime) return base;
      return `Once on ${moment(schedule.datetime + 'Z').format('MMM D, YYYY, HH:mm')}`;
    }
    if (type === 'daily') return `${base} at ${schedule.time || '?'}`;
    if (type === 'weekly') return `${base} (${schedule.dayOfWeek}) at ${schedule.time || '?'}`;
    if (type === 'monthly') return `${base} on day ${schedule.dayOfMonth} at ${schedule.time || '?'}`;
    if (type === 'hourly') return schedule.minute != null ? `${base} at :${String(schedule.minute).padStart(2, '0')}` : base;
    if (type === 'custom') return schedule.expression || base;
    return base;
  };

  const renderRunRow = (run) => {
    const isSuccess = run.status === 'SUCCESS';
    const isRunning = run.status === 'RUNNING';
    const isSubroutine = run.kind === 'SUBROUTINE_JOB';
    const ranAt = formatTs(run.startedAt || run.createdAt);
    const startedAtMoment = run.startedAt ? moment(run.startedAt) : null;
    const finishedAtMoment = run.finishedAt ? moment(run.finishedAt) : null;
    const durationMs = startedAtMoment?.isValid() && finishedAtMoment?.isValid() ?
      finishedAtMoment.diff(startedAtMoment) :
      null;
    return (
      <Stack key={run.runRn} direction='row' alignItems='flex-start' sx={{gap: '10px'}}>
        {isRunning ?
          <ScheduleOutlined sx={{fontSize: 14, color: '#2563EB', mt: '2px', flexShrink: 0}} /> :
          isSuccess ?
          <CheckCircleOutlined sx={{fontSize: 14, color: '#10B981', mt: '2px', flexShrink: 0}} /> :
          <ErrorOutlineOutlined sx={{fontSize: 14, color: '#EF4444', mt: '2px', flexShrink: 0}} />
        }
        <Stack sx={{gap: '3px', flex: 1, minWidth: 0}}>
          <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{gap: '8px'}}>
            <Stack direction='row' alignItems='center' sx={{gap: '6px', flexWrap: 'wrap'}}>
              <Typography sx={{fontSize: '11px', color: '#A8A49E'}}>
                {ranAt || '—'}{durationMs != null ? ` · ${durationMs}ms` : ''}
              </Typography>
              {isSubroutine && (
                <Box sx={{display: 'inline-flex', alignItems: 'center', px: '6px', py: '2px', borderRadius: '4px',
                  border: '1px solid #CDD7FF', backgroundColor: '#EEF1FF'}}>
                  <Typography sx={{fontSize: '10px', fontWeight: 600, color: '#6366F1', lineHeight: '100%'}}>
                    <Translate expression='routines.View.Subroutine' />
                  </Typography>
                </Box>
              )}
              {isRunning && (
                <Box sx={{display: 'inline-flex', alignItems: 'center', px: '6px', py: '2px', borderRadius: '4px',
                  border: '1px solid #93C5FD', backgroundColor: '#EFF6FF'}}>
                  <Typography sx={{fontSize: '10px', fontWeight: 600, color: '#2563EB', lineHeight: '100%'}}>
                    <Translate expression='routines.View.RunningStatus' />
                  </Typography>
                </Box>
              )}
            </Stack>
            {run.sessionRn && (
              <Box
                onClick={() => navigate(`/agent/chat?sessionRn=${run.sessionRn}`)}
                sx={{'display': 'inline-flex', 'alignItems': 'center', 'gap': '4px',
                  'cursor': 'pointer', 'color': '#A8A49E', '&:hover': {color: '#DA7955'}}}
              >
                <ChatOutlined sx={{fontSize: 13}} />
                <Typography sx={{fontSize: '11px', lineHeight: '100%'}}>View</Typography>
              </Box>
            )}
          </Stack>
          {run.summary && (
            <Typography sx={{fontSize: '12px', color: '#1A1916', lineHeight: '150%',
              overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical'}}>
              {run.summary}
            </Typography>
          )}
          {!isSuccess && run.error && (
            <Typography sx={{fontSize: '12px', color: '#EF4444', lineHeight: '150%'}}>{run.error}</Typography>
          )}
        </Stack>
      </Stack>
    );
  };

  if (!routine) {
    return (
      <Box sx={{width: '100%', p: '40px', boxSizing: 'border-box', backgroundColor: '#FFFFFF',
        display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#A8A49E', lineHeight: '150%'}}>
          <Translate expression='routines.View.Empty' />
        </Typography>
      </Box>
    );
  }

  const isActive = routine.status === 'ACTIVE';
  const lastRan = formatTs(routine.lastRunAt);
  const nextRun = routine.status === 'ACTIVE' ?
    formatTs(computeNextRunAt(routine.schedule, routine.lastRunAt)) :
    null;

  return (
    <Box sx={{width: '100%', p: '40px', boxSizing: 'border-box', backgroundColor: '#FFFFFF', overflowY: 'auto'}}>
      <Stack sx={{gap: '24px'}}>
        {/* Header */}
        <Stack direction='row' alignItems='flex-start' justifyContent='space-between' sx={{gap: '24px', flexWrap: 'wrap'}}>
          <Stack sx={{gap: '10px'}}>
            <Stack direction='row' alignItems='center' sx={{gap: '10px', flexWrap: 'wrap'}}>
              <Typography sx={{fontWeight: 400, fontSize: '22px', color: '#1A1916', lineHeight: '100%',
                textDecoration: 'underline', textDecorationColor: '#DA7955', textUnderlineOffset: '5px'}}>
                {routine.title || routine.routineRn}
              </Typography>
              <Box sx={{display: 'inline-flex', alignItems: 'center', px: '8px', py: '4px', borderRadius: '4px',
                border: isActive ? '1px solid #6EE7B7' : '1px solid #E8E4DE',
                backgroundColor: isActive ? '#ECFDF5' : '#F3F2EF'}}>
                <Typography sx={{fontSize: '11px', fontWeight: 400, color: isActive ? '#059669' : '#6B7280', lineHeight: '100%'}}>
                  <Translate expression={isActive ? 'routines.View.Active' : 'routines.View.Inactive'} />
                </Typography>
              </Box>
              {runningCount > 0 && (
                <Box sx={{display: 'inline-flex', alignItems: 'center', px: '8px', py: '4px', borderRadius: '4px',
                  border: '1px solid #93C5FD', backgroundColor: '#EFF6FF'}}>
                  <Typography sx={{fontSize: '11px', fontWeight: 400, color: '#2563EB', lineHeight: '100%'}}>
                    <Translate expression='routines.View.Running' substitute={{count: runningCount}} />
                  </Typography>
                </Box>
              )}
              {routine.executionMode === 'WAIT_FOR_APPROVAL' && (
                <Box sx={{display: 'inline-flex', alignItems: 'center', px: '8px', py: '4px', borderRadius: '4px',
                  border: '1px solid #FCD34D', backgroundColor: '#FFFBEB'}}>
                  <Typography sx={{fontSize: '11px', fontWeight: 400, color: '#B45309', lineHeight: '100%'}}>
                    <Translate expression='routines.View.WaitForApproval' />
                  </Typography>
                </Box>
              )}
            </Stack>
            {routine.description && (
              <Typography sx={{fontWeight: 400, fontSize: '14px', color: '#A8A49E', lineHeight: '150%'}}>
                {routine.description}
              </Typography>
            )}
          </Stack>

          <Stack direction='row' sx={{gap: '8px'}}>
            <Button
              onClick={onRunNow}
              disableElevation
              sx={{'minWidth': '0', 'borderRadius': '7px', 'px': '12px', 'py': '8px', 'fontWeight': 500,
                'fontSize': '12px', 'lineHeight': '100%', 'textTransform': 'none',
                'color': '#FFFFFF', 'backgroundColor': '#DA7955', '&:hover': {backgroundColor: '#DA7955', boxShadow: 'none'}}}
            >
              <Translate expression='routines.View.RunNow' />
            </Button>
            <Button
              onClick={onEdit}
              disableElevation
              sx={{'minWidth': '0', 'borderRadius': '7px', 'px': '12px', 'py': '8px', 'fontWeight': 500,
                'fontSize': '12px', 'lineHeight': '100%', 'textTransform': 'none',
                'color': '#FFFFFF', 'backgroundColor': '#DA7955', '&:hover': {backgroundColor: '#DA7955', boxShadow: 'none'}}}
            >
              <Translate expression='routines.View.Edit' />
            </Button>
            <Button
              onClick={onToggleStatus}
              variant='outlined'
              disableElevation
              sx={{'minWidth': '0', 'borderRadius': '7px', 'px': '12px', 'py': '8px', 'fontWeight': 500,
                'fontSize': '12px', 'lineHeight': '100%', 'textTransform': 'none',
                'color': '#8E8A84', 'borderColor': '#E8E4DE', 'backgroundColor': '#FFFFFF',
                '&:hover': {backgroundColor: '#FFFFFF', borderColor: '#E8E4DE', boxShadow: 'none'}}}
            >
              <Translate expression={isActive ? 'routines.View.Pause' : 'routines.View.Activate'} />
            </Button>
            <Button
              onClick={onDelete}
              variant='outlined'
              disableElevation
              sx={{'minWidth': '0', 'borderRadius': '7px', 'px': '12px', 'py': '8px', 'fontWeight': 500,
                'fontSize': '12px', 'lineHeight': '100%', 'textTransform': 'none',
                'color': '#EF4444', 'borderColor': '#FECACA', 'backgroundColor': '#FFFFFF',
                '&:hover': {backgroundColor: '#FEF2F2', borderColor: '#FECACA', boxShadow: 'none'}}}
            >
              <Translate expression='routines.View.Delete' />
            </Button>
          </Stack>
        </Stack>

        {/* Schedule */}
        <Stack sx={{gap: '10px'}}>
          <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
            <Translate expression='routines.View.Schedule' />
          </Typography>
          <Box sx={{border: '1px solid #E8E4DE', borderRadius: '12px', px: '14px', py: '12px', backgroundColor: '#FBFBFB'}}>
            <Stack direction='row' alignItems='center' sx={{gap: '8px'}}>
              <ScheduleOutlined sx={{fontSize: 14, color: '#A8A49E'}} />
              <Typography sx={{fontSize: '13px', color: '#1A1916', lineHeight: '150%'}}>
                {formatSchedule(routine.schedule)}
              </Typography>
            </Stack>
            {(lastRan || nextRun) && (
              <Stack direction='row' sx={{mt: '10px', gap: '24px', flexWrap: 'wrap'}}>
                {lastRan && (
                  <Stack sx={{gap: '4px'}}>
                    <Typography sx={{fontSize: '10px', fontWeight: 600, color: '#A8A49E', textTransform: 'uppercase'}}>
                      <Translate expression='routines.View.LastRan' />
                    </Typography>
                    <Typography sx={{fontSize: '12px', color: '#6B6760'}}>{lastRan}</Typography>
                  </Stack>
                )}
                {nextRun && (
                  <Stack sx={{gap: '4px'}}>
                    <Typography sx={{fontSize: '10px', fontWeight: 600, color: '#A8A49E', textTransform: 'uppercase'}}>
                      <Translate expression='routines.View.NextRun' />
                    </Typography>
                    <Typography sx={{fontSize: '12px', color: '#6B6760'}}>{nextRun}</Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </Box>
        </Stack>

        {/* Instructions */}
        {routine.instructions && (
          <Stack sx={{gap: '10px'}}>
            <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
              <Translate expression='routines.View.Instructions' />
            </Typography>
            <Box sx={{border: '1px solid #E8E4DE', borderRadius: '12px', px: '14px', py: '12px', backgroundColor: '#FBFBFB'}}>
              <Typography sx={{fontSize: '12px', fontWeight: 400, color: '#1A1916', lineHeight: '175%', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                {routine.instructions}
              </Typography>
            </Box>
          </Stack>
        )}

        {/* Run history */}
        {runHistory.length > 0 && (
          <Stack sx={{gap: '10px'}}>
            <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
              <Translate expression='routines.View.History' />
            </Typography>
            <Box sx={{border: '1px solid #E8E4DE', borderRadius: '12px', px: '14px', py: '12px', backgroundColor: '#FBFBFB'}}>
              <Stack sx={{gap: '16px'}}>
                {groupedRunHistory.map(({run, children}) => (
                  <Stack key={run.runRn} sx={{gap: '10px'}}>
                    {renderRunRow(run)}
                    {children.length > 0 && (
                      <Stack sx={{gap: '10px', pl: '24px', borderLeft: '2px solid #E8E4DE'}}>
                        {children.map((child) => renderRunRow(child))}
                      </Stack>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

RoutinesView.propTypes = {
  routine: PropTypes.shape({
    routineRn: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    instructions: PropTypes.string,
    status: PropTypes.string,
    executionMode: PropTypes.string,
    schedule: PropTypes.object,
    lastRunAt: PropTypes.string,
    nextRunAt: PropTypes.string,
  }),
  onRunNow: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default React.memo(RoutinesView);
